import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../../firebase/firebase';
import { describeGeoError } from './geoLocationError';
import ActiveEventBanner from '../events/ActiveEventBanner';
import './geoScenes.css';

const formatDistance = (meters) => {
  if (meters == null || !Number.isFinite(meters)) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

const proximityPercent = (distanceMeters, radiusMeters) => {
  if (distanceMeters == null || !Number.isFinite(distanceMeters) || !radiusMeters) return 0;
  if (distanceMeters <= radiusMeters) return 100;
  return Math.max(2, Math.min(100, Math.round((radiusMeters / distanceMeters) * 100)));
};

const haversineMeters = (aLat, aLng, bLat, bLng) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const aa = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(aa));
};
const HYSTERESIS_EXIT_BUFFER_METERS = 20;
const PROXIMITY_ENTER_CONFIRM_SAMPLES = 2;
const PROXIMITY_EXIT_CONFIRM_SAMPLES = 3;
const PROXIMITY_FILTER_ALPHA = 0.35;
const GEO_DIAGNOSTIC_MODE_KEY = 'geoDiagnosticMode';
const UNKNOWN_ARTIST_KEY = '__sin_artista__';

export default function GeoScenesPublic() {
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPosition, setUserPosition] = useState(null);
  const [userAccuracyMeters, setUserAccuracyMeters] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [diagnosticMode, setDiagnosticMode] = useState(false);
  const [filteredDistanceByScene, setFilteredDistanceByScene] = useState({});
  const [proximityDebugByScene, setProximityDebugByScene] = useState({});
  const distanceFilterRef = useRef({});
  const proximityStateRef = useRef({});
  const [nearbyStateByScene, setNearbyStateByScene] = useState({});
  const [selectedArtist, setSelectedArtist] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(query(
          collection(db, 'geoScenes'),
          where('isPublic', '==', true)
        ));
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

        const scenesWithPlacements = await Promise.all(list.map(async (scene) => {
          try {
            const placementsSnap = await getDocs(collection(db, 'geoScenes', scene.id, 'placements'));
            const placements = placementsSnap.docs
              .map((d) => ({ id: d.id, ...(d.data() || {}) }))
              .sort((a, b) => (Number(a.routeOrder ?? 9999) - Number(b.routeOrder ?? 9999)));
            return { scene, placements };
          } catch (e) {
            console.error('Error loading placements for scene', scene.id, e);
            return { scene, placements: [] };
          }
        }));

        // El artista se busca en vivo desde el modelo (por modelId) en vez de
        // confiar en la copia guardada en el placement, para que un cambio de
        // artista en el modelo se refleje sin tener que re-guardar cada punto.
        const modelIds = Array.from(new Set(
          scenesWithPlacements.flatMap(({ placements }) => placements.map((p) => p.modelId).filter(Boolean))
        ));
        const modelArtistById = {};
        await Promise.all(modelIds.map(async (modelId) => {
          try {
            const modelSnap = await getDoc(doc(db, 'models', modelId));
            if (modelSnap.exists()) {
              modelArtistById[modelId] = (modelSnap.data()?.artist || '').trim();
            }
          } catch (e) {
            console.error('Error loading model artist', modelId, e);
          }
        }));

        const withPlacements = scenesWithPlacements.map(({ scene, placements }) => {
          const artists = Array.from(new Set(
            placements
              .map((p) => (modelArtistById[p.modelId] ?? p.artist ?? '').trim())
              .filter(Boolean)
          ));
          return {
            ...scene,
            placementsCount: placements.length,
            primaryPlacement: placements[0] || null,
            artists
          };
        });

        setScenes(withPlacements);
        setError(null);
      } catch (e) {
        console.error(e);
        setError('No se pudieron cargar las escenas públicas');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const requestUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Este navegador no soporta geolocalización.');
      return;
    }
    setLocating(true);
    setLocationError(null);

    const onSuccess = (pos) => {
      setUserPosition([pos.coords.latitude, pos.coords.longitude]);
      setUserAccuracyMeters(Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null);
      setLocationError(null);
      setLocating(false);
    };

    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (err) => {
        console.error(err);
        // En celulares el primer fix GPS de alta precisión puede tardar más de
        // 12s (sobre todo en interiores). Si fue timeout, reintentamos una vez
        // con menor precisión (ubicación por red, mucho más rápida) en vez de
        // dejar al usuario sin nada.
        if (err.code === 3) {
          navigator.geolocation.getCurrentPosition(
            onSuccess,
            (err2) => {
              console.error(err2);
              setLocationError(describeGeoError(err2));
              setLocating(false);
            },
            { enableHighAccuracy: false, timeout: 20000, maximumAge: 30000 }
          );
          return;
        }
        setLocationError(describeGeoError(err));
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 20000 }
    );
  };

  useEffect(() => {
    requestUserLocation();
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setUserPosition([pos.coords.latitude, pos.coords.longitude]);
        setUserAccuracyMeters(Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null);
        setLocationError(null);
      },
      (err) => {
        console.error(err);
        setLocationError((prev) => prev || describeGeoError(err));
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 8000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    try {
      setDiagnosticMode(localStorage.getItem(GEO_DIAGNOSTIC_MODE_KEY) === '1');
    } catch {}
  }, []);

  const scenesWithDistance = useMemo(() => {
    return scenes.map((scene) => {
      const placementsCountEarly = scene.placementsCount || 0;
      const kindEarly = placementsCountEarly === 0 ? 'empty' : placementsCountEarly > 1 ? 'route' : 'single';
      if (!userPosition || !Array.isArray(scene.center) || scene.center.length !== 2) {
        return { ...scene, distanceMeters: null, isNearby: false, kind: kindEarly };
      }
      const dist = haversineMeters(
        Number(userPosition[0]),
        Number(userPosition[1]),
        Number(scene.center[0]),
        Number(scene.center[1])
      );
      const activationRadiusMeters = Math.max(10, Number(scene.activationRadiusMeters) || 120);
      return {
        ...scene,
        activationRadiusMeters,
        distanceMeters: dist,
        isNearby: dist <= activationRadiusMeters,
        kind: kindEarly
      };
    }).sort((a, b) => {
      if (a.distanceMeters == null && b.distanceMeters == null) return 0;
      if (a.distanceMeters == null) return 1;
      if (b.distanceMeters == null) return -1;
      return a.distanceMeters - b.distanceMeters;
    });
  }, [scenes, userPosition]);

  useEffect(() => {
    const nextFiltered = {};
    for (const scene of scenesWithDistance) {
      if (scene.distanceMeters == null) continue;
      const prev = Number(distanceFilterRef.current[scene.id]);
      const filtered = Number.isFinite(prev)
        ? prev + PROXIMITY_FILTER_ALPHA * (scene.distanceMeters - prev)
        : scene.distanceMeters;
      distanceFilterRef.current[scene.id] = filtered;
      nextFiltered[scene.id] = filtered;
    }
    setFilteredDistanceByScene((prev) => ({ ...prev, ...nextFiltered }));
  }, [scenesWithDistance]);

  const scenesWithSignals = useMemo(() => {
    return scenesWithDistance.map((scene) => ({
      ...scene,
      filteredDistanceMeters: filteredDistanceByScene[scene.id] ?? scene.distanceMeters
    })).sort((a, b) => {
      if (a.filteredDistanceMeters == null && b.filteredDistanceMeters == null) return 0;
      if (a.filteredDistanceMeters == null) return 1;
      if (b.filteredDistanceMeters == null) return -1;
      return a.filteredDistanceMeters - b.filteredDistanceMeters;
    });
  }, [scenesWithDistance, filteredDistanceByScene]);

  const artistIndex = useMemo(() => {
    const map = new Map();
    scenesWithSignals.forEach((scene) => {
      const artists = scene.artists?.length ? scene.artists : [UNKNOWN_ARTIST_KEY];
      artists.forEach((name) => {
        if (!map.has(name)) {
          map.set(name, {
            key: name,
            name: name === UNKNOWN_ARTIST_KEY ? 'Sin artista asignado' : name,
            sceneIds: new Set()
          });
        }
        map.get(name).sceneIds.add(scene.id);
      });
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.key === UNKNOWN_ARTIST_KEY) return 1;
      if (b.key === UNKNOWN_ARTIST_KEY) return -1;
      return b.sceneIds.size - a.sceneIds.size || a.name.localeCompare(b.name);
    });
  }, [scenesWithSignals]);

  const namedArtists = useMemo(
    () => artistIndex.filter((a) => a.key !== UNKNOWN_ARTIST_KEY),
    [artistIndex]
  );

  const visibleScenes = useMemo(() => {
    if (!selectedArtist) return scenesWithSignals;
    return scenesWithSignals.filter((scene) => {
      const artists = scene.artists?.length ? scene.artists : [UNKNOWN_ARTIST_KEY];
      return artists.includes(selectedArtist);
    });
  }, [scenesWithSignals, selectedArtist]);

  const nearbyScene = useMemo(
    () => scenesWithSignals.find((s) => nearbyStateByScene[s.id]),
    [scenesWithSignals, nearbyStateByScene]
  );
  useEffect(() => {
    const nextNearby = {};
    const nextDebug = {};
    for (const scene of scenesWithSignals) {
      const state = proximityStateRef.current[scene.id] || { inside: false, enterHits: 0, exitHits: 0 };
      if (scene.filteredDistanceMeters == null) {
        state.inside = false;
        state.enterHits = 0;
        state.exitHits = 0;
        proximityStateRef.current[scene.id] = state;
        nextNearby[scene.id] = false;
        nextDebug[scene.id] = { ...state };
        continue;
      }
      const enterThreshold = scene.activationRadiusMeters;
      const exitThreshold = scene.activationRadiusMeters + HYSTERESIS_EXIT_BUFFER_METERS;
      const d = Number(scene.filteredDistanceMeters);
      if (!state.inside) {
        if (d <= enterThreshold) state.enterHits += 1;
        else state.enterHits = 0;
      } else {
        if (d > exitThreshold) state.exitHits += 1;
        else state.exitHits = 0;
      }

      if (!state.inside && state.enterHits >= PROXIMITY_ENTER_CONFIRM_SAMPLES) {
        state.inside = true;
        state.enterHits = 0;
        state.exitHits = 0;
      } else if (state.inside && state.exitHits >= PROXIMITY_EXIT_CONFIRM_SAMPLES) {
        state.inside = false;
        state.enterHits = 0;
        state.exitHits = 0;
      }
      proximityStateRef.current[scene.id] = state;
      nextNearby[scene.id] = state.inside;
      nextDebug[scene.id] = { ...state };
    }
    setNearbyStateByScene(nextNearby);
    setProximityDebugByScene(nextDebug);
  }, [scenesWithSignals]);

  const locationStatus = userPosition ? 'active' : locating ? 'locating' : locationError ? 'error' : 'idle';

  return (
    <div className="geo-public-wrap">
      <header className="geo-public-header">
        <div>
          <span className="geo-eyebrow-badge">Museo Digital · Recorridos en RA</span>
          <h1>Escenas Geográficas</h1>
          <p>Explora obras individuales y rutas con múltiples piezas 3D ubicadas en coordenadas reales.</p>
        </div>
        <div className="geo-header-actions">
          <button
            className={`geo-btn ${diagnosticMode ? 'geo-btn-primary' : 'geo-btn-secondary'}`}
            onClick={() => {
              const next = !diagnosticMode;
              setDiagnosticMode(next);
              try { localStorage.setItem(GEO_DIAGNOSTIC_MODE_KEY, next ? '1' : '0'); } catch {}
            }}
            title="Muestra datos técnicos de proximidad (uso interno)"
          >
            <i className="bi bi-bug" /> {diagnosticMode ? 'Diagnóstico ON' : 'Diagnóstico OFF'}
          </button>
        </div>
      </header>

      <ActiveEventBanner />

      <div className={`geo-location-banner status-${locationStatus}`}>
        <div className="geo-location-banner-main">
          <span className={`geo-status-dot status-${locationStatus}`} />
          <div>
            <strong>
              {locationStatus === 'active' && 'Ubicación activa'}
              {locationStatus === 'locating' && 'Buscando tu ubicación...'}
              {locationStatus === 'error' && 'No se pudo ubicar'}
              {locationStatus === 'idle' && 'Detección por ubicación'}
            </strong>
            {userPosition ? (
              <p>
                Lat {userPosition[0].toFixed(5)} · Lng {userPosition[1].toFixed(5)}
                {userAccuracyMeters != null ? ` · precisión ±${Math.round(userAccuracyMeters)} m` : ''}
              </p>
            ) : (
              <p>{locationError || 'Habilitá tu ubicación para detectar automáticamente obras y rutas cercanas.'}</p>
            )}
          </div>
        </div>
        <button className="geo-btn geo-btn-secondary" onClick={requestUserLocation} disabled={locating}>
          <i className={`bi ${locating ? 'bi-arrow-repeat geo-spin' : 'bi-crosshair'}`} />
          {locating ? 'Localizando...' : 'Actualizar ubicación'}
        </button>
      </div>

      {!loading && namedArtists.length === 0 && scenesWithSignals.length > 0 && (
        <div className="geo-artist-filter geo-artist-filter-empty">
          <i className="bi bi-person-badge" />
          <div>
            <strong>Todavía no hay artistas asignados a las obras.</strong>
            <p className="geo-muted">
              Para poder elegir un artista y ver su recorrido, cargá su nombre en el campo
              «Artista / Escultor» de cada modelo desde el panel de administración.
            </p>
          </div>
        </div>
      )}

      {!loading && namedArtists.length > 0 && (
        <div className="geo-artist-filter">
          <div className="geo-artist-filter-title">
            <i className="bi bi-person-badge" /> Elegí un artista para ver su recorrido
          </div>
          <div className="geo-artist-chips">
            <button
              className={`geo-artist-chip ${!selectedArtist ? 'active' : ''}`}
              onClick={() => setSelectedArtist(null)}
            >
              <span className="geo-artist-avatar geo-artist-avatar-all"><i className="bi bi-grid-3x3-gap-fill" /></span>
              Todos los artistas
              <span className="geo-artist-count">{scenesWithSignals.length}</span>
            </button>
            {artistIndex.map((a) => (
              <button
                key={a.key}
                className={`geo-artist-chip ${selectedArtist === a.key ? 'active' : ''} ${a.key === UNKNOWN_ARTIST_KEY ? 'is-unknown' : ''}`}
                onClick={() => setSelectedArtist((prev) => (prev === a.key ? null : a.key))}
              >
                <span className="geo-artist-avatar">
                  {a.key === UNKNOWN_ARTIST_KEY ? <i className="bi bi-question-lg" /> : a.name.slice(0, 1).toUpperCase()}
                </span>
                {a.name}
                <span className="geo-artist-count">{a.sceneIds.size}</span>
              </button>
            ))}
          </div>
          {selectedArtist && (
            <p className="geo-artist-filter-hint">
              Mostrando {visibleScenes.length === 1 ? 'la zona' : 'las zonas'} donde <b>{artistIndex.find((a) => a.key === selectedArtist)?.name}</b> tiene obras cargadas.
            </p>
          )}
        </div>
      )}

      <AnimatePresence>
        {nearbyScene && (
          <motion.div
            className="geo-nearby-alert"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <span className="geo-nearby-pulse-dot" />
            <span>
              ¡Estás cerca de <b>{nearbyScene.name || 'una escena'}</b>! ({formatDistance(nearbyScene.distanceMeters)})
            </span>
            <Link className="geo-btn geo-btn-primary" to={`/geoscenes/${nearbyScene.id}`}>
              Entrar ahora <i className="bi bi-arrow-right" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="geo-admin-empty"><i className="bi bi-arrow-repeat geo-spin" /> Cargando escenas...</div>
      ) : error ? (
        <div className="geo-admin-empty">{error}</div>
      ) : scenesWithSignals.length === 0 ? (
        <div className="geo-admin-empty">No hay escenas públicas por ahora.</div>
      ) : visibleScenes.length === 0 ? (
        <div className="geo-admin-empty">
          Este artista todavía no tiene obras cargadas en ninguna zona.
        </div>
      ) : (
        <div className="geo-public-grid">
          <AnimatePresence>
            {visibleScenes.map((scene, idx) => {
              const isNear = !!nearbyStateByScene[scene.id];
              const pct = proximityPercent(scene.filteredDistanceMeters, scene.activationRadiusMeters);
              const thumbUrl = scene.primaryPlacement?.thumbnailUrl || '';
              return (
                <motion.article
                  key={scene.id}
                  className={`geo-public-card ${isNear ? 'is-near' : ''}`}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(idx, 6) * 0.05 }}
                >
                  <div className="geo-public-card-media">
                    {thumbUrl ? (
                      <img src={thumbUrl} alt={scene.primaryPlacement?.title || scene.name} />
                    ) : (
                      <div className="geo-public-card-media-placeholder">
                        <i className={`bi ${scene.kind === 'route' ? 'bi-signpost-split' : 'bi-gem'}`} />
                      </div>
                    )}
                    <span className={`geo-kind-badge kind-${scene.kind}`}>
                      {scene.kind === 'route' && (
                        <><i className="bi bi-signpost-split" /> Ruta · {scene.placementsCount} obras</>
                      )}
                      {scene.kind === 'single' && (
                        <><i className="bi bi-gem" /> Obra individual</>
                      )}
                      {scene.kind === 'empty' && (
                        <><i className="bi bi-hourglass-split" /> Próximamente</>
                      )}
                    </span>
                    {isNear && <span className="geo-near-badge"><i className="bi bi-broadcast" /> Cerca tuyo</span>}
                  </div>

                  <div className="geo-public-card-body">
                    <h3>{scene.name || 'Escena sin nombre'}</h3>
                    <p className="geo-muted geo-card-location"><i className="bi bi-geo-alt" /> {scene.location || 'Ubicación no definida'}</p>
                    {scene.artists?.length > 0 && (
                      <p className="geo-card-artists"><i className="bi bi-person-badge" /> {scene.artists.join(', ')}</p>
                    )}
                    <p className="geo-card-desc">{scene.description || 'Sin descripción.'}</p>

                    <div className="geo-proximity">
                      <div className="geo-proximity-bar">
                        <motion.div
                          className="geo-proximity-fill"
                          initial={false}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.6 }}
                        />
                      </div>
                      <div className={`geo-proximity-label ${isNear ? 'is-near' : ''}`}>
                        {scene.distanceMeters == null ? (
                          'Ubicación no disponible'
                        ) : isNear ? (
                          <><i className="bi bi-check-circle-fill" /> Dentro de la zona de la obra</>
                        ) : (
                          <>A {formatDistance(scene.distanceMeters)} · zona de {scene.activationRadiusMeters} m</>
                        )}
                      </div>
                    </div>

                    {diagnosticMode && (
                      <div className="geo-diagnostic-mini">
                        inside {isNear ? 'true' : 'false'} ·
                        enterHits {proximityDebugByScene[scene.id]?.enterHits ?? 0} ·
                        exitHits {proximityDebugByScene[scene.id]?.exitHits ?? 0} ·
                        cruda {scene.distanceMeters == null ? 'N/A' : `${scene.distanceMeters.toFixed(1)}m`} ·
                        filtrada {scene.filteredDistanceMeters == null ? 'N/A' : `${scene.filteredDistanceMeters.toFixed(1)}m`}
                      </div>
                    )}

                    <Link className="geo-btn geo-btn-primary geo-card-cta" to={`/geoscenes/${scene.id}`}>
                      {scene.kind === 'route' ? 'Explorar ruta' : 'Abrir visor geográfico'} <i className="bi bi-arrow-right" />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
